import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import './AvvisoLobby.css';

/** Quanto resta a schermo un avviso prima di sparire da solo. */
const DURATA_MS = 9_000;

/**
 * L'avviso di ciò che è accaduto alla coda mentre non si guardava.
 *
 * Serve perché il tavolo sopravvive all'overlay che l'ha aperto: la cosa può
 * finire — scadere dopo 10 minuti, o diventare una partita che l'avversario
 * abbandona prima della prima mossa — mentre a schermo non c'è niente di simile a
 * un lobby. Prima quei due eventi erano muti: `QueuedTableBanner` semplicemente
 * spariva, e chi aspettava restava a guardare uno schermo che non gli diceva
 * perché non stava più aspettando.
 *
 * `abortNotice` esisteva già nello store con la chiave i18n giusta dentro, ma
 * NESSUN componente lo leggeva: la frase «l'avversario è uscito prima
 * dell'inizio», tradotta in otto lingue, non è mai arrivata a schermo. Questo
 * componente è il posto che mancava, ed è uno solo per tutti e due i casi.
 *
 * Sparisce da sé: è una notizia, non una decisione da prendere. La ✕ c'è per chi
 * vuole liberare subito l'angolo (e perché un avviso che non si può chiudere è
 * un ostacolo su uno schermo da 360px).
 */
export default function AvvisoLobby() {
  const { t } = useTranslation();
  const avviso = useMultiplayerStore((s) => s.abortNotice);
  const pulisci = useMultiplayerStore((s) => s.clearAbortNotice);

  useEffect(() => {
    if (!avviso) return;
    const timer = setTimeout(pulisci, DURATA_MS);
    return () => clearTimeout(timer);
  }, [avviso, pulisci]);

  if (!avviso) return null;

  return (
    <div className="avviso-lobby" role="status" aria-live="polite">
      <span className="avviso-lobby-testo">{t(avviso)}</span>
      <button
        type="button"
        className="avviso-lobby-chiudi"
        aria-label={t('common.dismissNotice')}
        onClick={pulisci}
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
